import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';
import { logger } from '../../lib/logger';
import { createProgressPoller } from '../../lib/progressPoller';
import { compressImagesForUpload } from '../../lib/imageCompression';
import { isElevationRequired } from '../../lib/elevationErrors';
import {
  checkUploadReadiness,
  humanizeUploadError,
  uploadWithResilience,
} from '../../lib/resilientUpload';

const ALLOWED_MIME = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/heic',
  'image/heif',
];
const isAllowedByName = (name) => /\.(pdf|png|jpe?g|webp|heic|heif)$/i.test(name || '');

const filterValidFiles = (selectedFiles) =>
  selectedFiles.filter((f) => {
    const ok = ALLOWED_MIME.includes(f.type) || (!f.type && isAllowedByName(f.name));
    if (!ok) {
      toast.error(`Skipped "${f.name}" — unsupported type`);
      return false;
    }
    if (f.size > 50 * 1024 * 1024) {
      toast.error(`Skipped "${f.name}" — too large (max 50 MB)`);
      return false;
    }
    return true;
  });

const dropFailedHeic = (compressedAll, validFilesRaw) => {
  const validFiles = [];
  const rawForKept = [];
  compressedAll.forEach((compressed, i) => {
    if (compressed?.__heicTranscodeFailed) {
      toast.error(
        `Couldn't decode "${validFilesRaw[i].name}" (HEIC). Open it in Photos → Share → "Save as JPEG" and re-upload.`,
        { duration: 8000 }
      );
      return;
    }
    validFiles.push(compressed);
    rawForKept.push(validFilesRaw[i]);
  });
  return { validFiles, rawForKept };
};

export const useTimesheetScanner = () => {
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [patients, setPatients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [visibleCount, setVisibleCount] = useState(10);
  const [autoConfirming, setAutoConfirming] = useState(false);
  const [crossCheckTimesheetId, setCrossCheckTimesheetId] = useState(null);
  const [reviewQueue, setReviewQueue] = useState(null);
  const [needsElevation, setNeedsElevation] = useState(false);
  // Live server-backed progress for background OCR batches.
  const [activeBatches, setActiveBatches] = useState({});
  const batchPollersRef = useRef({});

  const fetchTimesheets = useCallback(async ({ silent = false } = {}) => {
    try {
      // The background poll (`silent=true`) MUST NOT flip the global
      // ``loading`` state — otherwise the whole list unmounts every 5 s
      // and the page flickers while the user is trying to read it.
      if (!silent) setLoading(true);
      const params = filterStatus !== 'all' ? { status: filterStatus } : {};
      const res = await api.get('/timesheets', { params });
      // Avoid gratuitous re-renders: only setState when something
      // actually changed. Deep-equal is overkill for this list; a
      // shallow fingerprint (id → status + updated_at) is enough to
      // detect the transitions the poll cares about.
      setTimesheets((prev) => {
        const next = res.data || [];
        if (prev.length === next.length) {
          let same = true;
          for (let i = 0; i < next.length; i += 1) {
            const a = prev[i];
            const b = next[i];
            if (
              !a || !b || a.id !== b.id ||
              a.status !== b.status ||
              a.updated_at !== b.updated_at
            ) {
              same = false;
              break;
            }
          }
          if (same) return prev;
        }
        return next;
      });
      setNeedsElevation(false);
    } catch (err) {
      if (isElevationRequired(err)) {
        // AZAI platform staff without an active elevation — surface the
        // banner instead of the misleading "Failed to load" toast. The
        // Dashboard's PlatformElevation dialog is the CTA target.
        setNeedsElevation(true);
        setTimesheets([]);
        logger.debug('[timesheets] elevation required', {
          op: 'timesheets_list',
          status: err?.response?.status,
        });
      } else if (!silent) {
        // Never toast on background poll failures — the next tick will
        // recover and the user shouldn't see error spam mid-scan.
        toast.error('Failed to load timesheets');
        logger.error('[timesheets] fetch failed', {
          op: 'timesheets_list',
          status: err?.response?.status,
        });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filterStatus]);

  const fetchDropdowns = useCallback(async () => {
    try {
      const res = await api.get('/timesheets/dropdowns/options');
      setPatients(res.data.patients || []);
      setEmployees(res.data.employees || []);
    } catch (err) {
      if (isElevationRequired(err)) {
        // Same elevation gate. Keep dropdowns empty silently.
        return;
      }
      logger.warn('Failed to load patient/employee dropdowns:', err?.message);
    }
  }, []);

  useEffect(() => {
    fetchTimesheets();
  }, [fetchTimesheets]);
  useEffect(() => {
    fetchDropdowns();
  }, [fetchDropdowns]);

  // Auto-poll while any timesheet is still being extracted by the
  // server-side background OCR job. Multi-page PDFs > 6 pages hit the
  // background path and land as status="processing" stubs; this loop
  // refreshes the list every 5 s (silently — no spinner, no toast on
  // transient failure) until every batch has settled.
  const hasProcessing = useMemo(
    () => timesheets.some((t) => t.status === 'processing'),
    [timesheets]
  );
  useEffect(() => {
    if (!hasProcessing) return undefined;
    const id = setInterval(() => {
      fetchTimesheets({ silent: true });
    }, 15000);
    return () => clearInterval(id);
  }, [hasProcessing, fetchTimesheets]);

  const counts = useMemo(() => {
    const c = {
      all: timesheets.length,
      processing: 0,
      pending_review: 0,
      extraction_failed: 0,
      confirmed: 0,
      submitted_to_evv: 0,
      claims_created: 0,
    };
    timesheets.forEach((t) => {
      c[t.status] = (c[t.status] || 0) + 1;
    });
    return c;
  }, [timesheets]);

  const readinessBuckets = useMemo(() => {
    const ready = [];
    const review = [];
    const blocked = [];
    timesheets.forEach((t) => {
      if (t.status !== 'pending_review') return;
      const lvl = t.readiness?.level;
      if (lvl === 'ready') ready.push(t);
      else if (lvl === 'review') review.push(t);
      else blocked.push(t);
    });
    return { ready, review, blocked };
  }, [timesheets]);

  const handleAutoConfirm = async () => {
    if (readinessBuckets.ready.length === 0) return;
    const n = readinessBuckets.ready.length;
     
    if (!window.confirm(`Auto-confirm ${n} timesheet${n === 1 ? '' : 's'} with AI confidence ≥ 90%?`)) {
      return;
    }
    setAutoConfirming(true);
    try {
      const res = await api.post('/timesheets/auto-confirm', { min_confidence: 90 });
      toast.success(
        `Confirmed ${res.data.confirmed_count}${res.data.skipped_count ? ` · ${res.data.skipped_count} skipped` : ''}`
      );
      fetchTimesheets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Auto-confirm failed');
    } finally {
      setAutoConfirming(false);
    }
  };

  const handleEnterReviewMode = () => {
    const queue = [
      ...readinessBuckets.review,
      ...readinessBuckets.blocked,
      ...readinessBuckets.ready,
    ].map((t) => t.id);
    if (queue.length === 0) {
      toast.info('No pending timesheets to review');
      return;
    }
    setReviewQueue(queue);
  };

  /**
   * Server-backed progress watcher for one background upload batch.
   *
   * Polls the tenant-scoped ``GET /api/timesheets/batch/{id}/status``
   * every 2s while pages are processing, feeding the visible progress
   * panel (stage + percent + pages done). Per approved rules:
   *  - stops immediately on terminal states (complete/partial/failed),
   *  - throttles to 10s while the tab is hidden,
   *  - tolerates transient network errors (next tick recovers),
   *  - never resets the session idle timer (plain GET polling),
   *  - duplicate-loop guarded (one poller per batch id),
   *  - batch ids persist in sessionStorage so a page refresh resumes
   *    progress for still-running batches.
   */
  const batchStorageKey = () =>
    `azai_active_batches_${localStorage.getItem('currentAgencyId') || 'default'}`;

  const _readSavedBatches = () => {
    try {
      return JSON.parse(sessionStorage.getItem(batchStorageKey()) || '[]');
    } catch {
      return [];
    }
  };

  const _rememberBatch = (batchId, filename) => {
    try {
      const arr = _readSavedBatches().filter((b) => b.batch_id !== batchId);
      arr.push({ batch_id: batchId, filename });
      sessionStorage.setItem(batchStorageKey(), JSON.stringify(arr));
    } catch {
      /* storage unavailable — progress still works for this session */
    }
  };

  const _forgetBatch = (batchId) => {
    try {
      const arr = _readSavedBatches().filter((b) => b.batch_id !== batchId);
      sessionStorage.setItem(batchStorageKey(), JSON.stringify(arr));
    } catch {
      /* ignore */
    }
  };

  const dismissBatch = useCallback((batchId) => {
    batchPollersRef.current[batchId]?.stop?.();
    delete batchPollersRef.current[batchId];
    _forgetBatch(batchId);
    setActiveBatches((prev) => {
      const next = { ...prev };
      delete next[batchId];
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startBatchWatch = useCallback(
    (batchId, filename) => {
      if (!batchId || batchPollersRef.current[batchId]) return; // duplicate-loop guard
      _rememberBatch(batchId, filename);
      setActiveBatches((prev) => ({
        ...prev,
        [batchId]: { batch_id: batchId, filename, stage: 'upload_received', percent: 0 },
      }));
      batchPollersRef.current[batchId] = createProgressPoller({
        fetchStatus: async () => {
          const res = await api.get(`/timesheets/batch/${batchId}/status`);
          return { terminal: !!res.data.complete, data: res.data };
        },
        onUpdate: (d) =>
          setActiveBatches((prev) =>
            prev[batchId] ? { ...prev, [batchId]: { ...prev[batchId], ...d } } : prev
          ),
        onDone: (d) => {
          delete batchPollersRef.current[batchId];
          _forgetBatch(batchId);
          setActiveBatches((prev) =>
            prev[batchId] ? { ...prev, [batchId]: { ...prev[batchId], ...d } } : prev
          );
          fetchTimesheets({ silent: true });
          // Auto-clear the finished card after a short read window.
          setTimeout(() => dismissBatch(batchId), 12000);
        },
        onError: (err) => {
          delete batchPollersRef.current[batchId];
          _forgetBatch(batchId);
          setActiveBatches((prev) => {
            const next = { ...prev };
            delete next[batchId];
            return next;
          });
          logger.warn?.(`[batch-progress] batch ${batchId} watch ended`, err?.message);
        },
        intervalMs: 2000,
        hiddenIntervalMs: 10000,
        maxDurationMs: 15 * 60 * 1000,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [fetchTimesheets, dismissBatch]
  );

  // Refresh recovery: resume progress for batches that were still running
  // before the page reloaded. Cleanup stops every poller on unmount.
  useEffect(() => {
    _readSavedBatches().forEach((b) => startBatchWatch(b.batch_id, b.filename));
    const pollers = batchPollersRef.current;
    return () => {
      Object.values(pollers).forEach((p) => p?.stop?.());
      batchPollersRef.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadOne = async (file, ctx) => {
    try {
      const data = await uploadWithResilience(file, {
        onProgress: (msg) => setUploadStatus(msg),
      });
      ctx.totalCreated += data.count || 0;
      // Same-agency duplicate detection (SHA-256): warn the biller
      // before they review the same sheet twice — never a block.
      if (data.document_warning) {
        toast.warning(`${file.name}: ${data.document_warning}`, { duration: 12000 });
      }
      const skipped = data.skipped_pages || [];
      if (skipped.length) {
        ctx.totalSkipped += skipped.length;
      }
      // Track background-mode uploads so the summary toast can tell the
      // user their large PDF is still being scanned server-side. The
      // hook's own poll loop refreshes the list every 15 s until all
      // processing rows settle.
      if (data.mode === 'background') {
        ctx.backgroundCount += data.count || 0;
        if (data.batch_id) {
          ctx.backgroundBatches.push({
            batch_id: data.batch_id,
            filename: file.name,
            total: data.count || 0,
          });
          // Start the visible progress bar immediately — server-backed,
          // 2s polling, stops on terminal.
          startBatchWatch(data.batch_id, file.name);
        }
      }
    } catch (err) {
      ctx.anyFailed = true;
      toast.error(humanizeUploadError(err, file.name), { duration: 10000 });
    } finally {
      ctx.completed += 1;
      const next = ctx.validFiles[ctx.completed];
      if (next) {
        setUploadStatus(
          `Scanning ${ctx.completed + 1} of ${ctx.validFiles.length}: ${next.name}…`
        );
      }
    }
  };

  const handleFiles = async (selectedFiles) => {
    const validFilesRaw = filterValidFiles(selectedFiles);
    if (validFilesRaw.length === 0) return;

    setUploading(true);
    setUploadStatus('Checking server availability…');

    // Pre-flight readiness probe — refuses to start the upload if the
    // pod is under memory pressure. Better to warn the user up front
    // than to transfer 50 MB and then eat a 502.
    const ready = await checkUploadReadiness();
    if (!ready.ok) {
      toast.error(ready.message, { duration: 8000 });
      setUploading(false);
      setUploadStatus('');
      return;
    }
    if (ready.degraded) {
      toast.warning(ready.message, { duration: 6000 });
    }

    setUploadStatus('Preparing files…');

    const compressedAll = await compressImagesForUpload(validFilesRaw);
    const { validFiles, rawForKept } = dropFailedHeic(compressedAll, validFilesRaw);
    if (validFiles.length === 0) {
      setUploading(false);
      setUploadStatus('');
      return;
    }

    const compressionSavings = rawForKept.reduce(
      (acc, raw, i) => acc + (raw.size - validFiles[i].size),
      0
    );
    if (compressionSavings > 256 * 1024) {
      const mb = (compressionSavings / 1024 / 1024).toFixed(1);
      toast.info(`Compressed images saved ${mb} MB upload bandwidth`);
    }

    const ctx = {
      totalCreated: 0,
      backgroundCount: 0,
      backgroundBatches: [],
      totalSkipped: 0,
      anyFailed: false,
      completed: 0,
      validFiles,
    };
    setUploadStatus(`Scanning 1 of ${validFiles.length}: ${validFiles[0].name}…`);

    const CONCURRENCY = 4;
    let cursor = 0;
    const workers = Array.from(
      { length: Math.min(CONCURRENCY, validFiles.length) },
      async () => {
        while (cursor < validFiles.length) {
          const idx = cursor;
          cursor += 1;
           
          await uploadOne(validFiles[idx], ctx);
        }
      }
    );
    await Promise.all(workers);

    setUploading(false);
    setUploadStatus('');

    if (ctx.totalCreated > 0) {
      const fileWord = validFiles.length === 1 ? 'file' : 'files';
      const tsWord = ctx.totalCreated === 1 ? 'timesheet' : 'timesheets';
      const skippedSuffix = ctx.totalSkipped
        ? ` (${ctx.totalSkipped} non-timesheet page${ctx.totalSkipped === 1 ? '' : 's'} skipped)`
        : '';
      if (ctx.backgroundCount > 0) {
        // Some (or all) came back in background mode — the rows are
        // already visible as "Scanning…" and the progress panel shows
        // live per-batch stage + percent (server-backed, 2s polling).
        toast.success(
          `Uploaded ${ctx.totalCreated} ${tsWord} from ${validFiles.length} ${fileWord}${skippedSuffix}. ` +
          `${ctx.backgroundCount} still being scanned in the background — watch the progress bar above.`,
          { duration: 8000 }
        );
      } else {
        toast.success(`Scanned ${ctx.totalCreated} ${tsWord} from ${validFiles.length} ${fileWord}${skippedSuffix}`);
      }
    } else if (!ctx.anyFailed) {
      toast.info('No timesheets extracted');
    }
    fetchTimesheets();
  };

  const runWithBusy = async (id, op, errMessage) => {
    setBusyId(id);
    try {
      await op();
    } catch (err) {
      toast.error(err.response?.data?.detail || errMessage);
    } finally {
      setBusyId(null);
    }
  };

  const handleInlineSave = (id, payload) =>
    runWithBusy(
      id,
      async () => {
        await api.patch(`/timesheets/${id}/inline-update`, payload);
        toast.success('Draft saved');
        fetchTimesheets();
      },
      'Save failed'
    );

  const handleConfirm = (id, payload) =>
    runWithBusy(
      id,
      async () => {
        await api.put(`/timesheets/${id}/confirm`, payload);
        toast.success('Timesheet confirmed');
        fetchTimesheets();
      },
      'Confirmation failed'
    );

  const handleDelete = (id) => {
     
    if (!window.confirm('Delete this timesheet?')) return Promise.resolve();
    return runWithBusy(
      id,
      async () => {
        await api.delete(`/timesheets/${id}`);
        toast.success('Timesheet deleted');
        fetchTimesheets();
      },
      'Delete failed'
    );
  };

  const handleSubmitEvv = (id) => {
    setCrossCheckTimesheetId(id);
  };

  const handleCreateClaims = (id) =>
    runWithBusy(
      id,
      async () => {
        const res = await api.post(`/timesheets/${id}/create-claims`, {
          timesheet_id: id,
          service_code: 'T1019',
          place_of_service: '12',
        });
        toast.success(res.data.message);
        fetchTimesheets();
      },
      'Claim creation failed'
    );

  return {
    timesheets,
    patients,
    employees,
    loading,
    uploading,
    uploadStatus,
    filterStatus,
    setFilterStatus,
    busyId,
    visibleCount,
    setVisibleCount,
    autoConfirming,
    crossCheckTimesheetId,
    setCrossCheckTimesheetId,
    reviewQueue,
    setReviewQueue,
    counts,
    readinessBuckets,
    needsElevation,
    activeBatches,
    dismissBatch,
    fetchTimesheets,
    handleAutoConfirm,
    handleEnterReviewMode,
    handleFiles,
    handleInlineSave,
    handleConfirm,
    handleDelete,
    handleSubmitEvv,
    handleCreateClaims,
  };
};
